import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./admin.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NAVBAR_LOGO = "https://customer-assets.emergentagent.com/job_transform-beauty-4/artifacts/gb9lfe95_logo%20son.png";

const HEADING_FONTS = ["Cormorant Garamond", "Playfair Display", "Cinzel", "EB Garamond", "Lora", "Libre Baskerville"];
const BODY_FONTS = ["Nunito", "Manrope", "Poppins", "Lato", "DM Sans", "Inter", "Mulish"];

const NAV_ITEMS = [
  { key: "hero", label: "Ana Sayfa" },
  { key: "philosophy", label: "Marka Hikayesi" },
  { key: "services", label: "Hizmetler" },
  { key: "about", label: "Hakkımızda" },
  { key: "contact", label: "İletişim" },
  { key: "images", label: "Görseller" },
  { key: "settings", label: "Ayarlar" },
  { key: "backup", label: "Yedekleme" },
  { key: "contacts", label: "Randevu Talepleri" },
  { key: "gallery", label: "Galeri" },
];

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("aphea_admin_token")}` };
}

function SaveMsg({ msg }) {
  if (!msg) return null;
  return <div className={`save-msg ${msg.type}`}>{msg.type === "success" ? "✓ " : "✗ "}{msg.text}</div>;
}

function ImageInput({ label, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);

  const handleFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      onChange(BACKEND_URL + data.url);
    } catch {
      alert("Yükleme başarısız. Dosya boyutunu veya formatını kontrol edin.");
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div className="img-input-wrap">
      <label className="admin-label" style={{ marginBottom: 10, display: "block" }}>{label}</label>
      {value && <img src={value} alt="" className="img-preview" onError={e => { e.target.style.display = "none"; }} />}
      <div className="img-input-row">
        <input type="text" className="admin-input" value={value || ""} onChange={e => onChange(e.target.value)} placeholder="Görsel URL'si girin..." />
        <button type="button" className="upload-btn" onClick={() => ref.current?.click()} disabled={uploading}>
          {uploading ? "Yükleniyor..." : "Dosya Seç"}
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" onChange={handleFile} style={{ display: "none" }} />
    </div>
  );
}

function useSectionSave(content, onSave, sectionKey) {
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async (sectionData) => {
    setSaving(true);
    setMsg(null);
    try {
      await onSave({ ...content, [sectionKey]: sectionData });
      setMsg({ type: "success", text: "Kaydedildi" });
    } catch {
      setMsg({ type: "error", text: "Kaydetme hatası oluştu" });
    }
    setSaving(false);
    setTimeout(() => setMsg(null), 4000);
  };

  return { saving, msg, save };
}

// ── Section Editors ────────────────────────────────

function HeroEditor({ content, onSave }) {
  const [d, setD] = useState(content?.hero || {});
  const { saving, msg, save } = useSectionSave(content, onSave, "hero");
  useEffect(() => { setD(content?.hero || {}); }, [content?.hero]);
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));

  return (
    <div className="editor-wrap">
      <h2 className="editor-title">Ana Sayfa (Hero)</h2>
      <p className="editor-info">Ziyaretçilerin ilk gördüğü bölümün içeriklerini düzenleyin.</p>
      <div className="form-grid">
        <div className="form-group">
          <label className="admin-label">Ana Başlık</label>
          <textarea className="admin-input admin-textarea" rows={3} value={d.headline || ""} onChange={e => set("headline", e.target.value)} />
          <span style={{ fontSize: "0.7rem", color: "#aaa" }}>Satır sonu için \\n kullanın</span>
        </div>
        <div className="form-group">
          <label className="admin-label">Alt Başlık / Açıklama</label>
          <textarea className="admin-input admin-textarea" rows={2} value={d.sub || ""} onChange={e => set("sub", e.target.value)} />
        </div>
        <div className="form-group-row">
          <div className="form-group">
            <label className="admin-label">CTA Butonu Metni</label>
            <input className="admin-input" value={d.cta || ""} onChange={e => set("cta", e.target.value)} />
          </div>
          <div className="form-group">
            <label className="admin-label">WhatsApp Butonu Metni</label>
            <input className="admin-input" value={d.waCta || ""} onChange={e => set("waCta", e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="admin-label">Konum Metni</label>
          <input className="admin-input" value={d.location || ""} onChange={e => set("location", e.target.value)} />
        </div>
      </div>
      <div className="editor-footer">
        <SaveMsg msg={msg} />
        <button className="save-btn" onClick={() => save(d)} disabled={saving}>{saving ? "Kaydediliyor..." : "Kaydet"}</button>
      </div>
    </div>
  );
}

function PhilosophyEditor({ content, onSave }) {
  const [d, setD] = useState(content?.philosophy || {});
  const { saving, msg, save } = useSectionSave(content, onSave, "philosophy");
  useEffect(() => { setD(content?.philosophy || {}); }, [content?.philosophy]);
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));

  return (
    <div className="editor-wrap">
      <h2 className="editor-title">Marka Hikayesi</h2>
      <div className="form-grid">
        {[["label", "Bölüm Etiketi"], ["q1", "Alıntı 1"], ["q2", "Alıntı 2"]].map(([k, lbl]) => (
          <div className="form-group" key={k}>
            <label className="admin-label">{lbl}</label>
            <input className="admin-input" value={d[k] || ""} onChange={e => set(k, e.target.value)} />
          </div>
        ))}
        {[["body1", "Metin Paragrafı 1"], ["body2", "Metin Paragrafı 2"], ["brand", "Marka İfadesi (Bold)"]].map(([k, lbl]) => (
          <div className="form-group" key={k}>
            <label className="admin-label">{lbl}</label>
            <textarea className="admin-input admin-textarea" rows={3} value={d[k] || ""} onChange={e => set(k, e.target.value)} />
          </div>
        ))}
      </div>
      <div className="editor-footer">
        <SaveMsg msg={msg} />
        <button className="save-btn" onClick={() => save(d)} disabled={saving}>{saving ? "Kaydediliyor..." : "Kaydet"}</button>
      </div>
    </div>
  );
}

function ServicesEditor({ content, onSave }) {
  const [d, setD] = useState(content?.services || {});
  const { saving, msg, save } = useSectionSave(content, onSave, "services");
  useEffect(() => { setD(content?.services || {}); }, [content?.services]);
  const setField = (k, v) => setD(p => ({ ...p, [k]: v }));
  const setItem = (i, k, v) => {
    const items = [...(d.items || [])];
    items[i] = { ...items[i], [k]: v };
    setD(p => ({ ...p, items }));
  };

  return (
    <div className="editor-wrap">
      <h2 className="editor-title">Hizmetler</h2>
      <div className="form-grid">
        {[["label", "Bölüm Etiketi"], ["headline", "Başlık"], ["sub", "Alt Başlık"]].map(([k, lbl]) => (
          <div className="form-group" key={k}>
            <label className="admin-label">{lbl}</label>
            <input className="admin-input" value={d[k] || ""} onChange={e => setField(k, e.target.value)} />
          </div>
        ))}
      </div>
      <h3 className="subsection-title">Hizmet Kartları</h3>
      {(d.items || []).map((item, i) => (
        <div className="service-item-editor" key={i}>
          <div className="service-item-num">{i + 1}</div>
          <div className="form-grid" style={{ flex: 1 }}>
            <div className="form-group">
              <label className="admin-label">Başlık</label>
              <input className="admin-input" value={item.title || ""} onChange={e => setItem(i, "title", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="admin-label">Açıklama</label>
              <textarea className="admin-input admin-textarea" rows={3} value={item.desc || ""} onChange={e => setItem(i, "desc", e.target.value)} />
            </div>
          </div>
        </div>
      ))}
      <div className="editor-footer">
        <SaveMsg msg={msg} />
        <button className="save-btn" onClick={() => save(d)} disabled={saving}>{saving ? "Kaydediliyor..." : "Kaydet"}</button>
      </div>
    </div>
  );
}

function AboutEditor({ content, onSave }) {
  const [d, setD] = useState(content?.about || {});
  const { saving, msg, save } = useSectionSave(content, onSave, "about");
  useEffect(() => { setD(content?.about || {}); }, [content?.about]);
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));

  return (
    <div className="editor-wrap">
      <h2 className="editor-title">Hakkımızda</h2>
      <div className="form-grid">
        {[["label", "Etiket"], ["headline", "Başlık"], ["badge", "Rozet Metni (Çok Yakında)"], ["location", "Konum"]].map(([k, lbl]) => (
          <div className="form-group" key={k}>
            <label className="admin-label">{lbl}</label>
            <input className="admin-input" value={d[k] || ""} onChange={e => set(k, e.target.value)} />
          </div>
        ))}
        <div className="form-group">
          <label className="admin-label">Açıklama</label>
          <textarea className="admin-input admin-textarea" rows={4} value={d.desc || ""} onChange={e => set("desc", e.target.value)} />
        </div>
      </div>
      <div className="editor-footer">
        <SaveMsg msg={msg} />
        <button className="save-btn" onClick={() => save(d)} disabled={saving}>{saving ? "Kaydediliyor..." : "Kaydet"}</button>
      </div>
    </div>
  );
}

function ContactEditor({ content, onSave }) {
  const [d, setD] = useState(content?.contact || {});
  const { saving, msg, save } = useSectionSave(content, onSave, "contact");
  useEffect(() => { setD(content?.contact || {}); }, [content?.contact]);
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  const setService = (i, v) => { const s = [...(d.services || [])]; s[i] = v; set("services", s); };

  return (
    <div className="editor-wrap">
      <h2 className="editor-title">İletişim / Randevu</h2>
      <div className="form-grid">
        {[["label", "Etiket"], ["headline", "Başlık"], ["nameLabel", "Ad Soyad Alanı Etiketi"], ["phoneLabel", "Telefon Alanı Etiketi"], ["serviceLabel", "Hizmet Seçimi Etiketi"], ["msgLabel", "Mesaj Alanı Etiketi"], ["submit", "Gönder Butonu"], ["waCta", "WhatsApp CTA Butonu"]].map(([k, lbl]) => (
          <div className="form-group" key={k}>
            <label className="admin-label">{lbl}</label>
            <input className="admin-input" value={d[k] || ""} onChange={e => set(k, e.target.value)} />
          </div>
        ))}
        {[["sub", "Alt Başlık"], ["waNote", "WhatsApp Notu"], ["igNote", "Instagram Notu"], ["success", "Başarı Mesajı"], ["error", "Hata Mesajı"]].map(([k, lbl]) => (
          <div className="form-group" key={k}>
            <label className="admin-label">{lbl}</label>
            <textarea className="admin-input admin-textarea" rows={2} value={d[k] || ""} onChange={e => set(k, e.target.value)} />
          </div>
        ))}
      </div>
      <h3 className="subsection-title">Hizmet Dropdown Listesi</h3>
      {(d.services || []).map((s, i) => (
        <div className="form-group" key={i} style={{ marginBottom: 8 }}>
          <label className="admin-label">Hizmet {i + 1}</label>
          <input className="admin-input" value={s} onChange={e => setService(i, e.target.value)} />
        </div>
      ))}
      <div className="editor-footer">
        <SaveMsg msg={msg} />
        <button className="save-btn" onClick={() => save(d)} disabled={saving}>{saving ? "Kaydediliyor..." : "Kaydet"}</button>
      </div>
    </div>
  );
}

function ImagesEditor({ content, onSave }) {
  const [d, setD] = useState(content?.images || {});
  const { saving, msg, save } = useSectionSave(content, onSave, "images");
  useEffect(() => { setD(content?.images || {}); }, [content?.images]);
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));

  const fields = [
    ["heroBg", "Hero Arka Plan Görseli"],
    ["brandStory", "Marka Hikayesi Görseli"],
    ["moodBoard", "Mood Şeridi Görseli"],
    ["venue", "Hakkımızda / Mekan Görseli"],
    ["logoWhite", "Logo (Beyaz – Footer & Watermark)"],
    ["logoNavbar", "Logo (Navbar – Sol Üst)"],
  ];

  return (
    <div className="editor-wrap">
      <h2 className="editor-title">Görseller</h2>
      <p className="editor-info">URL yapıştırın veya bilgisayarınızdan yükleyin (maks. 10 MB • jpeg, png, webp, gif)</p>
      {fields.map(([k, lbl]) => (
        <ImageInput key={k} label={lbl} value={d[k] || ""} onChange={v => set(k, v)} />
      ))}
      <div className="editor-footer">
        <SaveMsg msg={msg} />
        <button className="save-btn" onClick={() => save(d)} disabled={saving}>{saving ? "Kaydediliyor..." : "Kaydet"}</button>
      </div>
    </div>
  );
}

function SettingsEditor({ content, onSave }) {
  const [d, setD] = useState(content?.settings || {});
  const { saving, msg, save } = useSectionSave(content, onSave, "settings");
  useEffect(() => { setD(content?.settings || {}); }, [content?.settings]);
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));

  return (
    <div className="editor-wrap">
      <h2 className="editor-title">Ayarlar</h2>
      <h3 className="subsection-title">Yazı Tipleri</h3>
      <div className="form-grid-2">
        <div className="form-group">
          <label className="admin-label">Başlık Fontu</label>
          <select className="admin-input admin-select" value={d.headingFont || "Cormorant Garamond"} onChange={e => set("headingFont", e.target.value)}>
            {HEADING_FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="admin-label">Metin Fontu</label>
          <select className="admin-input admin-select" value={d.bodyFont || "Manrope"} onChange={e => set("bodyFont", e.target.value)}>
            {BODY_FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
          </select>
        </div>
      </div>
      <h3 className="subsection-title">Renk Paleti</h3>
      <div className="form-grid-2">
        {[["primaryColor", "Ana Renk (Burgundy)"], ["accentColor", "Vurgu Rengi (Gold)"], ["backgroundColor", "Arka Plan Rengi"]].map(([k, lbl]) => (
          <div className="form-group color-group" key={k}>
            <label className="admin-label">{lbl}</label>
            <div className="color-row">
              <input type="color" className="color-swatch" value={d[k] || "#000000"} onChange={e => set(k, e.target.value)} />
              <input className="admin-input" value={d[k] || ""} onChange={e => set(k, e.target.value)} placeholder="#HEX" />
            </div>
          </div>
        ))}
      </div>
      <h3 className="subsection-title">İletişim & Sosyal Medya</h3>
      <div className="form-grid">
        {[["whatsappNumber", "WhatsApp Numarası (905...)"], ["instagramHandle", "Instagram (@ olmadan)"], ["phoneNumber", "Telefon Numarası"], ["siteTitle", "Browser Tab Başlığı"], ["mapsUrl", "Google Maps URL"]].map(([k, lbl]) => (
          <div className="form-group" key={k}>
            <label className="admin-label">{lbl}</label>
            <input className="admin-input" value={d[k] || ""} onChange={e => set(k, e.target.value)} />
          </div>
        ))}
      </div>
      <div className="editor-footer">
        <SaveMsg msg={msg} />
        <button className="save-btn" onClick={() => save(d)} disabled={saving}>{saving ? "Kaydediliyor..." : "Kaydet"}</button>
      </div>
    </div>
  );
}

function BackupSection() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchBackups = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/backups`, { headers: authHeaders() });
      setBackups(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchBackups(); }, []);

  const downloadNew = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/backup/download`, { headers: authHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aphea_backup_${new Date().toISOString().split("T")[0]}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      setTimeout(fetchBackups, 1000);
    } catch { alert("Yedekleme oluşturulurken hata oluştu."); }
    setDownloading(false);
  };

  const downloadBackup = async (name) => {
    try {
      const res = await axios.get(`${API}/admin/backups/${name}`, { headers: authHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert("İndirme sırasında hata oluştu."); }
  };

  const fmt = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;

  return (
    <div className="editor-wrap">
      <h2 className="editor-title">Yedekleme</h2>
      <p className="editor-info">Her Pazar saat 03:00'te otomatik yedek oluşturulur. Manuel yedek almak için aşağıdaki butona tıklayın. Zip dosyası tüm kaynak kodları içerir ve başka bir sunucuda kurulabilir.</p>
      <button className="save-btn" onClick={downloadNew} disabled={downloading} style={{ marginBottom: 32 }}>
        {downloading ? "Hazırlanıyor... (Bu birkaç saniye sürebilir)" : "Yeni Yedek Oluştur & İndir"}
      </button>
      <h3 className="subsection-title">Mevcut Yedekler</h3>
      {loading ? <p style={{ color: "#877E75" }}>Yükleniyor...</p> : (
        <div className="backup-list">
          {backups.length === 0 ? (
            <p className="empty-state">Henüz oluşturulmuş yedek bulunmuyor.</p>
          ) : (
            backups.map(b => (
              <div className="backup-item" key={b.name}>
                <div>
                  <div className="backup-name">{b.name}</div>
                  <div className="backup-meta">{new Date(b.created).toLocaleString("tr-TR")} &bull; {fmt(b.size)}</div>
                </div>
                <button className="dl-btn" onClick={() => downloadBackup(b.name)}>İndir</button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ContactsTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/contacts`, { headers: authHeaders() })
      .then(r => setRows(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="editor-wrap" style={{ maxWidth: "100%" }}>
      <h2 className="editor-title">Randevu Talepleri</h2>
      <p className="editor-info">Formdan gelen en son 200 talep listeleniyor.</p>
      {loading ? <p style={{ color: "#877E75" }}>Yükleniyor...</p> : (
        rows.length === 0 ? (
          <p className="empty-state">Henüz randevu talebi bulunmuyor.</p>
        ) : (
          <div className="table-wrap">
            <table className="contacts-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Ad Soyad</th>
                  <th>Telefon</th>
                  <th>Hizmet</th>
                  <th>Mesaj</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: "nowrap" }}>{r.created_at ? new Date(r.created_at).toLocaleString("tr-TR") : "—"}</td>
                    <td>{r.name}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{r.phone}</td>
                    <td>{r.service || "—"}</td>
                    <td>{r.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

// ── Gallery Section ─────────────────────────────────
function GallerySection() {
  const [photos, setPhotos]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [toggling, setToggling] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const dragItem     = useRef(null);
  const dragOverItem = useRef(null);

  const fetchPhotos = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/gallery`, { headers: authHeaders() });
      setPhotos(data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchPhotos(); }, []);

  const toggle = async (filename) => {
    setToggling(filename);
    try {
      await axios.put(`${API}/admin/gallery/${encodeURIComponent(filename)}/toggle`, {}, { headers: authHeaders() });
      await fetchPhotos();
    } catch (e) {}
    setToggling(null);
  };

  const handleDragStart = (i) => { dragItem.current = i; };
  const handleDragEnter = (i) => { dragOverItem.current = i; };

  const handleDrop = async () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) return;

    const reordered = [...photos];
    const [moved] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, moved);
    dragItem.current     = null;
    dragOverItem.current = null;

    setPhotos(reordered);
    setSaving(true);
    try {
      await axios.put(`${API}/admin/gallery/reorder`, reordered.map(p => p.filename), { headers: authHeaders() });
      setSavedMsg("Sıralama kaydedildi ✓");
      setTimeout(() => setSavedMsg(""), 2500);
    } catch (e) {
      setSavedMsg("Hata oluştu");
    }
    setSaving(false);
  };

  const thumbUrl = (id) => `https://lh3.googleusercontent.com/d/${id}=w200`;

  return (
    <div className="editor-wrap" style={{ maxWidth: "100%" }}>
      <h2 className="editor-title">Galeri Yönetimi</h2>
      <p className="editor-info">
        Fotoğrafları <strong>sürükleyerek</strong> sıralayın. Gizlemek için "Gizle" butonuna tıklayın.{" "}
        <strong>{photos.filter(p => p.visible).length}</strong> aktif,{" "}
        <strong>{photos.filter(p => !p.visible).length}</strong> gizli.
        {saving && <span style={{ marginLeft: 10, color: "#C9A96E" }}>Kaydediliyor…</span>}
        {savedMsg && <span style={{ marginLeft: 10, color: "#2A6B2A" }}>{savedMsg}</span>}
      </p>
      {loading ? (
        <p style={{ color: "#877E75" }}>Yükleniyor...</p>
      ) : (
        <div className="gallery-admin-grid">
          {photos.map((p, i) => (
            <div
              key={p.filename}
              className={`gallery-admin-item${p.visible ? "" : " hidden"}`}
              draggable
              onDragStart={() => handleDragStart(i)}
              onDragEnter={() => handleDragEnter(i)}
              onDragEnd={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              data-testid={`gallery-item-${p.filename}`}
            >
              <div className="gallery-drag-handle" title="Sürükleyerek sırala">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/>
                  <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                  <circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
                </svg>
              </div>
              <img src={thumbUrl(p.drive_id)} alt={p.filename} loading="lazy" />
              <div className="gallery-admin-info">
                <span className="gallery-admin-name">{p.filename}</span>
                <button
                  className={`gallery-admin-btn${p.visible ? " hide-btn" : " show-btn"}`}
                  onClick={() => toggle(p.filename)}
                  disabled={toggling === p.filename}
                  data-testid={`gallery-toggle-${p.filename}`}
                >
                  {toggling === p.filename ? "…" : p.visible ? "Gizle" : "Göster"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────

export default function Dashboard() {
  const [activeSection, setActiveSection] = useState("hero");
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const adminEmail = localStorage.getItem("aphea_admin_email") || "";

  useEffect(() => {
    axios.get(`${API}/admin/content`, { headers: authHeaders() })
      .then(r => setContent(r.data))
      .catch(() => {
        localStorage.removeItem("aphea_admin_token");
        navigate("/admin/login");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("aphea_admin_token");
    localStorage.removeItem("aphea_admin_email");
    navigate("/admin/login");
  };

  const handleSave = async (updatedContent) => {
    await axios.put(`${API}/admin/content`, updatedContent, { headers: authHeaders() });
    setContent(updatedContent);
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner" />
        <p>Yönetim paneli yükleniyor...</p>
      </div>
    );
  }

  const sectionMap = {
    hero: <HeroEditor content={content} onSave={handleSave} />,
    philosophy: <PhilosophyEditor content={content} onSave={handleSave} />,
    services: <ServicesEditor content={content} onSave={handleSave} />,
    about: <AboutEditor content={content} onSave={handleSave} />,
    contact: <ContactEditor content={content} onSave={handleSave} />,
    images: <ImagesEditor content={content} onSave={handleSave} />,
    settings: <SettingsEditor content={content} onSave={handleSave} />,
    backup: <BackupSection />,
    contacts: <ContactsTable />,
    gallery: <GallerySection />,
  };

  return (
    <div className="admin-root">
      <aside className="admin-sidebar">
        <div className="sidebar-logo">
          <img src={NAVBAR_LOGO} alt="APHEA" className="sidebar-logo-img" />
          <span className="sidebar-brand">Admin</span>
        </div>
        <nav className="sidebar-nav">
          {NAV_ITEMS.map(item => (
            <button
              key={item.key}
              className={`sidebar-item ${activeSection === item.key ? "active" : ""}`}
              onClick={() => setActiveSection(item.key)}
              data-testid={`sidebar-${item.key}`}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <p className="sidebar-user">{adminEmail}</p>
          <button className="logout-btn" onClick={handleLogout} data-testid="logout-btn">Çıkış Yap</button>
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-header">
          <h1 className="admin-header-title">APHEA Yönetim Paneli</h1>
          <a href="/" target="_blank" rel="noopener noreferrer" className="preview-link">Siteyi Görüntüle →</a>
        </header>
        <div className="admin-content">
          {sectionMap[activeSection]}
        </div>
      </main>
    </div>
  );
}
